import React, { FunctionComponent, useEffect, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { Card, FormControl, Grid, InputLabel, makeStyles, MenuItem, Select } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { PERSON_HISTORY_CATEGORY_LIST } from 'src/store/app.constants';
import { AuditEventList, AuditEvent as AuditEventType } from 'src/store/modules/application/types';
import TableEmptyState from 'src/components/TableView/TableEmptyState';
import AuditHistoryNode from '../../../LicenseDetailTabs/Tabs/AuditHistory/AuditHistoryNode';
import { displayCurrencyAmount, getFullAddress } from 'src/utils/utils';

type HistoryProps = {
    personId: string;
    historyList: AuditEventList;
    getHistoryList: Function;
    loading: boolean;
};

type keyValueRecord = {
    parentField: string | null;
    field: string;
    fieldName: string;
    updatedValue: any;
};

//TODO: Find an alternate library function as a replacement
export const prependArticle = (word: string) => {
    const firstLetter = word[0];
    const isUpperCase = firstLetter === firstLetter.toUpperCase();
    if ('aeiou'.includes(firstLetter.toLowerCase())) return `${isUpperCase ? 'An' : 'an'} ${word.toLowerCase()}`;
    else return `${isUpperCase ? 'A' : 'a'} ${word.toLowerCase()}`;
};

const getFieldName = (field: string) => _.startCase(field);

const getPhrase = (auditEvent: AuditEventType, selectedCategory: string) => {
    const { user, entityType } = auditEvent;
    const { oldData, newData } = auditEvent.details;
    const keyValueArray: keyValueRecord[] = [];
    const phrase: string[] = [];
    const deletedProperties: string[] = [];
    const traverse = (objectTree: any, field: string, parentField: string, path?: string) => {
        if (typeof objectTree === 'object' && !!objectTree) {
            for (const key in objectTree) {
                if (parentField === 'person' || parentField === 'additionalData')
                    traverse(
                        objectTree[key],
                        key,
                        parentField,
                        `${key.match(/line[0-3]/y) ? 'Address - ' : ''}${getFieldName(key)}`,
                    );
                else if (parentField === 'type' && key === 'name') traverse(objectTree[key], key, parentField, 'Type');
                else
                    traverse(
                        objectTree[key],
                        key,
                        parentField,
                        `${path ? path : getFieldName(parentField)} - ${getFieldName(key)}`,
                    );
            }
        } else {
            !!objectTree &&
                field !== 'id' &&
                keyValueArray.push({
                    parentField,
                    field,
                    fieldName: path || getFieldName(field),
                    updatedValue: objectTree,
                });
            return;
        }
    };

    const getDeletedProperties = (newDataTree: any, oldDataTree: any, path?: string) => {
        if (typeof newDataTree === 'object' && typeof oldDataTree === 'object' && !!newDataTree && !!oldDataTree) {
            const newDataKeys: string[] = Object.keys(newDataTree);
            const oldDataKeys: string[] = Object.keys(oldDataTree);
            oldDataKeys.forEach((key) => {
                if (key !== 'registrationCode') {
                    if (!newDataKeys.includes(key)) deletedProperties.push(path ? path : getFieldName(key));
                    else if (['additionalData', 'person', 'address'].includes(key))
                        getDeletedProperties(newDataTree[key], oldDataTree[key]);
                    else
                        getDeletedProperties(
                            newDataTree[key],
                            oldDataTree[key],
                            path
                                ? `${path} - ${getFieldName(key)}`
                                : `${key.match(/line[0-3]/y) ? 'Address - ' : ''}${getFieldName(key)}`,
                        );
                }
            });
        } else {
            if (!newDataTree) path && deletedProperties.push(path);
        }
    };

    if (oldData && newData) {
        // Existing record was updated
        switch (entityType) {
            case 'Person':
                if (['All', 'Contacts'].includes(selectedCategory)) {
                    const createNewContactPhrases = (newContact: any) => {
                        phrase.push(
                            `${user?.name} added a new ${
                                newContact.contactType ? newContact.contactType.name : ''
                            } Contact`,
                        );
                        for (const field in newContact) {
                            if (!newContact[field] || ['id', 'contactType', 'registrationCode'].includes(field))
                                continue;
                            if (field === 'address') {
                                for (const addressField in newContact.address) {
                                    const value = newContact.address[addressField];
                                    value?.length > 0 &&
                                        phrase.push(
                                            `${user?.name} set Address - ${_.startCase(addressField)} to ${value}`,
                                        );
                                }
                            } else {
                                newContact[field]?.length > 0 &&
                                    phrase.push(`${user?.name} set ${_.startCase(field)} to ${newContact[field]}`);
                            }
                        }
                    };
                    if (newData.contacts?.length === oldData.contacts?.length) {
                        // existing contact was updated
                        const createContactUpdatePhrases = (oldValue: string, newValue: string, fieldName: string) => {
                            if (!!oldValue && oldValue.length > 0 && !!newValue && newValue.length > 0) {
                                phrase.push(
                                    `${user?.name} changed Contact ${
                                        fieldName.match(/line[0-3]/y) ? 'Address - ' : ''
                                    }${_.startCase(fieldName)} from ${oldValue} to ${newValue}`,
                                );
                            } else if (!!newValue && newValue.length > 0) {
                                phrase.push(
                                    `${user?.name} changed Contact ${
                                        fieldName.match(/line[0-3]/y) ? 'Address - ' : ''
                                    }${_.startCase(fieldName)} to ${newValue}`,
                                );
                            } else if (!!oldValue && oldValue.length > 0) {
                                phrase.push(
                                    `${user?.name} deleted Contact ${
                                        fieldName.match(/line[0-3]/y) ? 'Address - ' : ''
                                    }${_.startCase(fieldName)} value`,
                                );
                            }
                        };
                        newData.contacts?.forEach((contact: any, index: number) => {
                            for (const field in contact) {
                                if (field === 'id') {
                                    createNewContactPhrases(contact);
                                    continue;
                                }
                                let oldValue: any, newValue: any;
                                oldValue =
                                    !!oldData.contacts && oldData.contacts[index] && oldData.contacts[index][field];
                                newValue = contact[field];
                                let fieldName = _.startCase(field);
                                if (field === 'address') {
                                    for (const addressField in newValue) {
                                        createContactUpdatePhrases(
                                            oldValue && oldValue[addressField],
                                            newValue[addressField],
                                            addressField,
                                        );
                                    }
                                    continue;
                                }
                                if (field === 'contactType') {
                                    oldValue = oldValue?.name;
                                    newValue = newValue?.name;
                                    fieldName = 'Type';
                                }
                                createContactUpdatePhrases(oldValue, newValue, fieldName);
                            }
                        });
                    } else if (newData.contacts?.length > oldData.contacts?.length) {
                        // new contact(s) added:
                        const newContacts = newData.contacts.filter((newContact: any) => !!newContact && newContact.id);
                        newContacts.forEach((newContact: any) => {
                            createNewContactPhrases(newContact);
                        });
                    } else {
                        // contact(s) deleted:
                        const deletedContacts = oldData.contacts?.filter(
                            (oldContact: any) =>
                                !!oldContact &&
                                !newData.contacts?.some(
                                    (newContact: any) => !!newContact && oldContact.id === newContact.id,
                                ),
                        );
                        !!deletedContacts &&
                            deletedContacts.forEach(({ name }: { name: string }) => {
                                if (name) {
                                    phrase.push(`${user?.name} deleted Contact - ${name}`);
                                } else {
                                    phrase.push(`${user?.name} deleted a contact`); // To accomodate API-response inconsistencies for pre-existing contacts
                                }
                            });
                    }
                }

                if (['All', 'Details'].includes(selectedCategory)) {
                    // existing Licensee was updated
                    const createPhrases = (oldValue: string, newValue: string, fieldName: string) => {
                        if (!!oldValue && oldValue.length > 0 && !!newValue && newValue.length > 0) {
                            phrase.push(
                                `${user?.name} changed licensee ${
                                    fieldName.match(/line[0-3]/y) ? 'Address - ' : ''
                                }${_.startCase(fieldName)} from ${oldValue} to ${newValue}`,
                            );
                        } else if (!!newValue && newValue.length > 0) {
                            phrase.push(
                                `${user?.name} changed licensee ${
                                    fieldName.match(/line[0-3]/y) ? 'Address - ' : ''
                                }${_.startCase(fieldName)} to ${newValue}`,
                            );
                        }
                    };
                    for (const field of Object.keys(newData)) {
                        if (
                            field === 'lastUpdatedOn' ||
                            field === 'lastUpdatedBy' ||
                            field === 'contacts' ||
                            field === 'documents' ||
                            field === 'registrationCode' ||
                            field === 'userProfileId'
                        )
                            continue;
                        let oldValue: any, newValue: any;
                        oldValue = !!oldData && oldData[field];
                        newValue = newData[field];

                        if (field === 'address') {
                            for (const addressField in newValue) {
                                createPhrases(oldValue && oldValue[addressField], newValue[addressField], addressField);
                            }
                            continue;
                        }

                        if (field === 'additionalData') {
                            for (const field in newValue) {
                                createPhrases(oldValue && oldValue[field], newValue[field], field);
                            }
                            continue;
                        }
                        createPhrases(oldValue, newValue, field);
                    }
                    if (!oldData.documents) {
                        getDeletedProperties(newData, oldData);
                        deletedProperties.forEach((prop) =>
                            phrase.push(`${user?.name} deleted licensee ${prop} value`),
                        );
                    }
                }

                if (selectedCategory === 'Files' || selectedCategory === 'All') {
                    if (
                        newData.documents &&
                        ((oldData.documents && oldData.documents.length < newData.documents.length) ||
                            !oldData.documents)
                    ) {
                        // new document(s) were added::
                        newData.documents.forEach((document: any) => {
                            if (document) {
                                phrase.push(
                                    `${user?.name} uploaded file '${document.fileName}' of type ${document.metadata.documenttype}`,
                                );
                            }
                        });
                    } else if (oldData.documents) {
                        // document(s) were deleted::
                        let deletedFiles: string[] = [];
                        if (!newData.documents) {
                            deletedFiles = oldData.documents.filter((document: any) => document);
                        } else if (newData.documents && oldData.documents.length > newData.documents.length) {
                            deletedFiles = oldData.documents.filter((document: any) => {
                                if (!document) return false;
                                for (const newDocument of newData.documents)
                                    if (newDocument && document.id === newDocument.id) return false;
                                return true;
                            });
                        }
                        deletedFiles.forEach((file: any) => {
                            phrase.push(`${user?.name} deleted file '${file.fileName}'`);
                        });
                    }
                }
                break;
            case 'Event':
                for (const field of Object.keys(newData)) {
                    if (
                        field === 'id ' ||
                        field === 'lastUpdatedOn' ||
                        field === 'lastUpdatedBy' ||
                        field === 'licenses' ||
                        field === 'participants'
                    )
                        continue;
                    if (typeof newData[field] === 'object' && !!newData[field]) {
                        traverse(newData[field], field, field);
                    } else {
                        !!newData[field] &&
                            keyValueArray.push({
                                parentField: null,
                                field,
                                fieldName: getFieldName(field),
                                updatedValue: newData[field],
                            });
                    }
                }
                keyValueArray.forEach((property: keyValueRecord) => {
                    phrase.push(
                        `${user?.name} changed appointment ${
                            property.fieldName.startsWith('Appointment')
                                ? property.fieldName.substring(11)
                                : property.fieldName
                        } to ${
                            (property.fieldName === getFieldName('startDateTime') ||
                                property.fieldName === getFieldName('endDateTime')) &&
                            moment(property.updatedValue)?.isValid()
                                ? moment(property.updatedValue).format('YYYY-MM-DD HH:mm:ss')
                                : property.updatedValue
                        }`,
                    );
                });

                if (newData.participants?.length || oldData.participants?.length) {
                    let oldParticipants = _.differenceWith(oldData?.participants, newData?.participants, _.isEqual);
                    let newParticipants = _.differenceWith(newData?.participants, oldData?.participants, _.isEqual);

                    if (newData?.participants && oldData?.participants) {
                        const deletedParticipants = oldParticipants
                            ?.filter(Boolean)
                            .map((deletedParticipant: any) => deletedParticipant?.name);
                        const addedParticipants = newParticipants
                            ?.filter(Boolean)
                            .map((newParticipant: any) => newParticipant?.name);

                        !!deletedParticipants.length &&
                            phrase.push(
                                `${user?.name} deleted appointment participant(s): ${deletedParticipants.join(', ')}`,
                            );

                        !!addedParticipants.length &&
                            phrase.push(
                                `${user?.name} added appointment participant(s): ${addedParticipants.join(', ')}`,
                            );
                    }
                }
                break;
            default:
                break;
        }
    } else if (oldData) {
        // Existing record was deleted
        switch (entityType) {
            case 'Event':
                if (oldData?.type?.name && oldData?.startDateTime) {
                    phrase.push(
                        ` ${user?.name} deleted ${
                            oldData?.type?.name ? prependArticle(oldData?.type?.name.toLowerCase()) : ''
                        } appointment - ${moment(oldData?.startDateTime).format('L')}`,
                    );
                }
                break;
        }
    } else {
        switch (entityType) {
            case 'Person':
                if (selectedCategory === 'All' || selectedCategory === 'Details') {
                    newData.type && oldData === null && phrase.push(`Licensee added by ${user?.name}`);
                    for (const field in newData) {
                        if (
                            field === 'lastUpdatedOn' ||
                            field === 'lastUpdatedBy' ||
                            field === 'id' ||
                            field === 'registrationCode' ||
                            field === 'userProfileId'
                        )
                            continue;
                        if (typeof newData[field] === 'object' && !!newData[field]) {
                            traverse(newData[field], field, field);
                        } else {
                            !!newData[field] &&
                                keyValueArray.push({
                                    parentField: null,
                                    field,
                                    fieldName: getFieldName(field),
                                    updatedValue: newData[field],
                                });
                        }
                    }
                    keyValueArray.forEach((property: keyValueRecord) => {
                        phrase.push(`${user?.name} set ${property.fieldName} to ${property.updatedValue}`);
                    });
                }
                break;

            case 'License':
                if (selectedCategory === 'All' || selectedCategory === 'License') {
                    if (newData.type?.name) {
                        phrase.push(`License added by ${user?.name}`);
                        phrase.push(`${user?.name} set License Type to ${newData.type?.name}`);
                        newData.licenseNumber &&
                            phrase.push(`${user?.name} set License Number to ${newData.licenseNumber}`);
                    }
                }
                break;
            case 'JournalEntry':
                !!newData.lineItems &&
                    newData.lineItems.forEach((item: any) => {
                        const { type, amount, description, account } = item;
                        if (account?.id !== '000000000000000000000001') {
                            //TODO: Handle liability/equity accounts
                            if (type === 'credit') {
                                phrase.push(
                                    `${user?.name} added a charge of ${displayCurrencyAmount(amount)} ${
                                        description?.length ? " with description '" + description + "'" : ''
                                    }`,
                                );
                            } else if (type === 'debit') {
                                phrase.push(
                                    `${user?.name} added a deposit of ${displayCurrencyAmount(amount)} ${
                                        description?.length ? " with description '" + description + "'" : ''
                                    }`,
                                );
                            }
                        }
                    });
                break;
            case 'Event':
                if (!!newData.startDateTime && moment(newData.startDateTime).isValid()) {
                    phrase.push(
                        `${user?.name} added an ${newData.allDay ? 'all day' : ''} appointment - ${moment(
                            newData.startDateTime,
                        ).format('L')}
                        ${
                            !!newData?.endDateTime && moment(newData.endDateTime).isValid()
                                ? 'ending on ' + moment(newData.endDateTime).format('L')
                                : ''
                        }`,
                    );
                }

                newData.type?.name && phrase.push(`${user?.name} set appointment Type to ${newData.type?.name}`);
                if (!!newData.participants?.length) {
                    let participantList = Array.from(newData.participants, (participant: any) => participant.name);
                    phrase.push(`${user?.name} set participant(s) to: ${participantList.join(', ')}`);
                }
                !!newData.address &&
                    phrase.push(`${user?.name} set appointment location to: ${getFullAddress(newData.address)}`);
                break;
            case 'Comment':
                phrase.push(`${user?.name} added a comment`);
                break;
        }
    }
    return phrase;
};

const History: FunctionComponent<HistoryProps> = (props) => {
    const { personId, historyList, getHistoryList, loading } = props;

    const classes = makeStyles(() => ({
        formControl: {
            marginLeft: 15,
            marginBottom: 15,
            minWidth: 160,
        },
        historyList: {
            padding: 25,
        },
    }))();

    const [selectedCategory, setSelectedCategory] = useState('All');
    const handleSelectionChange = (event: any) => setSelectedCategory(event.target.value);

    useEffect(() => {
        getHistoryList(personId, selectedCategory);
    }, [personId, getHistoryList, selectedCategory]);

    const HistoryLogs: FunctionComponent = () => {
        const phraseArray: { phrase: string[]; date: string; id: string }[] = [];
        let historyFilter = (auditEvent: AuditEventType) => !!auditEvent;
        if (selectedCategory === 'Contacts') {
            historyFilter = (auditEvent: AuditEventType) =>
                !!auditEvent.details.newData?.contacts || !!auditEvent.details.oldData?.contacts;
        } else if (selectedCategory === 'Details') {
            historyFilter = (auditEvent: AuditEventType) =>
                !auditEvent.details.newData?.contacts || !auditEvent.details.oldData?.contacts;
        } else if (selectedCategory === 'Files') {
            historyFilter = (auditEvent: AuditEventType) =>
                !!auditEvent.details.newData?.documents || !!auditEvent.details.oldData?.documents;
        } else if (selectedCategory === 'Comment') {
            historyFilter = (auditEvent: AuditEventType) =>
                !!auditEvent.details.newData?.text && auditEvent.details.newData.reference?.type === 'Person';
        } else {
            historyFilter = (auditEvent: AuditEventType) =>
                (!!auditEvent.details.newData || !!auditEvent.details.oldData) &&
                (!!auditEvent.details?.newData?.reference?.type
                    ? auditEvent.details?.newData?.reference?.type === 'Person'
                    : true);
        }
        const historyLogsToDisplay: AuditEventType[] | undefined = historyList?.filter(historyFilter);
        historyLogsToDisplay?.forEach((auditEvent) => {
            const { timestamp, id } = auditEvent;
            if (!!id && !!timestamp) {
                const phrase = getPhrase(auditEvent, selectedCategory);
                phrase.length > 0 && phraseArray.push({ phrase, date: auditEvent.timestamp, id: auditEvent.id });
            }
        });

        if (phraseArray.length > 0)
            return (
                <>
                    {phraseArray.map(({ id, date, phrase }) => (
                        <AuditHistoryNode key={id} date={date} description={phrase} />
                    ))}
                </>
            );
        else
            return (
                <TableEmptyState
                    title="No history for this record"
                    message="All history logs will be displayed here."
                />
            );
    };

    return (
        <>
            <FormControl className={classes.formControl}>
                <InputLabel id="person-history-category-label">Category</InputLabel>
                <Select
                    labelId="person-history-category-label"
                    id="person-history-category"
                    defaultValue={PERSON_HISTORY_CATEGORY_LIST[0].value}
                    value={selectedCategory}
                    onChange={handleSelectionChange}
                >
                    {PERSON_HISTORY_CATEGORY_LIST.map((category) => (
                        <MenuItem key={category.id} value={category.value}>
                            {category.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Card className={classes.historyList}>
                {loading ? (
                    <div>
                        {[1, 2, 3].map((element) => (
                            <Grid key={element} container spacing={2}>
                                <Grid item sm={2}>
                                    <Skeleton />
                                </Grid>
                                <Grid item sm={5}>
                                    <Skeleton />
                                    <Skeleton />
                                    <Skeleton />
                                    <Skeleton />
                                </Grid>
                            </Grid>
                        ))}
                    </div>
                ) : (
                    <HistoryLogs />
                )}
            </Card>
        </>
    );
};
export default History;
